/**
 * Which `--mg-*` token a muscle display group draws in.
 *
 * The one place the six groups meet the six colours, so "the same colour in the day module and in
 * the week strip" — the canon's own legend line — is true by construction rather than by three
 * components agreeing. It is `ui/` rather than `model/` because a CSS custom-property name is a
 * rendering fact, not a domain one, and `entities/plan-draft` carries no colour at all for the same
 * reason.
 *
 * **Ember is not in this palette, and its absence is the point.** The bars are a categorical scale
 * over six neutral-to-mid hues; the accent marks a real action or a real state, roughly four times
 * per screen. A bar wearing the accent would compete with the lock chip and the focused control for
 * the trainer's eye, and it means neither of those things.
 *
 * ── It moved here from `pages/plan-builder/ui/` on its second consumer ───────────────────────
 * The week card drew it first; `features/plan-day-programming` draws the same bars in the day dock,
 * and a feature may not import from a page. `shared/` may not import from `entities/`, so the union
 * below is declared here rather than imported — and that is the one thing this move costs, because
 * `MuscleDisplayGroup` is where the six groups are really decided.
 *
 * **The exhaustiveness survives at the CALL SITE.** Every caller passes a `MuscleDisplayGroup`, so a
 * seventh display group added to the entity stops assigning to this parameter and every call breaks
 * — which is the same red build the old local `Record` gave, just one file further out. What no
 * longer happens automatically is this table growing a row; that is the compile error's job to
 * demand.
 */

/** The six display buckets, structurally identical to `entities/plan-draft`'s `MuscleDisplayGroup`. */
export type MuscleColourGroup = 'chest' | 'back' | 'legs' | 'shoulders' | 'arms' | 'core';

const TOKENS: Record<MuscleColourGroup, string> = {
  chest: 'var(--mg-klatka)',
  back: 'var(--mg-plecy)',
  legs: 'var(--mg-nogi)',
  shoulders: 'var(--mg-barki)',
  arms: 'var(--mg-ramiona)',
  core: 'var(--mg-core)',
};

/** The six, in the canon's left-to-right legend order. */
export const MUSCLE_COLOUR_ORDER: readonly MuscleColourGroup[] = [
  'chest',
  'back',
  'legs',
  'shoulders',
  'arms',
  'core',
];

export function muscleColour(group: MuscleColourGroup): string {
  return TOKENS[group];
}

/**
 * ── The SOURCE side of the six: which backend group lands in which bucket ────────────────────
 *
 * The fourteen-to-six rollup, keyed by the backend's lower-cased `MuscleGroup` enum name. It lived
 * in `entities/plan-draft`'s `muscleVolume` until 17 September 2026 and moved here whole — not
 * copied — when the exercise library's row needed the same answer: `entities/` may not import
 * `entities/`, so a row in `entities/exercise-library` could not ask the plan draft which hue a
 * movement is read by, and writing the table a second time would be two opinions about one
 * clustering. `plan-draft`'s `displayGroupOf` now reads this, under its own domain name and its own
 * type. Its exhaustive test is unchanged and still the guard.
 *
 * **`Other` and `FullBody` fold into `core`, and that is an owner-flagged arbitrary default.**
 * Execution ADR §2: "`FullBody` and `Other` have no bucket in the six-colour display palette. They
 * fold into Core. This is arbitrary and is recorded so it is a decision rather than an accident; a
 * test forces it to be explicit." Dropping them instead would silently lose a plank's whole
 * contribution — an exercise the trainer deliberately programmed would count for nothing — which is
 * worse than a bucket that is merely debatable.
 *
 * An EXHAUSTIVE record over the enum rather than a lookup with a fallback: a fifteenth backend
 * group must break a test, not land quietly in `core` because the fallback caught it.
 */
const COLOUR_GROUP_BY_SOURCE = {
  chest: 'chest',
  back: 'back',
  shoulders: 'shoulders',
  biceps: 'arms',
  triceps: 'arms',
  forearms: 'arms',
  quads: 'legs',
  hamstrings: 'legs',
  glutes: 'legs',
  calves: 'legs',
  abs: 'core',
  obliques: 'core',
  other: 'core',
  fullbody: 'core',
} as const satisfies Record<string, MuscleColourGroup>;

/** Every backend group name this build knows, lower-cased. */
export const MUSCLE_COLOUR_SOURCE_GROUPS: readonly string[] = Object.keys(COLOUR_GROUP_BY_SOURCE);

/**
 * One muscle group name → its display bucket, or `null` when this build has never heard of it.
 *
 * Takes the backend's PascalCase enum names AND this app's own lower-case slugs, because it
 * lower-cases what it is given and the two vocabularies are the same words in different casing.
 *
 * Null rather than a fallback bucket, deliberately. A group the app does not know contributes
 * nothing, which keeps the volume totals honest, and `plan-draft`'s exhaustive test is what
 * guarantees the null branch is unreachable for every group the backend actually emits today.
 */
export function muscleColourGroupOf(source: string | null | undefined): MuscleColourGroup | null {
  if (source == null) return null;
  const key = source.trim().toLowerCase();
  // `Object.hasOwn`, never `in`: `in` walks the prototype chain, so a wire value of `"constructor"`
  // or `"toString"` would test true and then index to a function off `Object.prototype`. The result
  // is typed `MuscleColourGroup` and would be carried as one all the way to a bar's colour.
  return Object.hasOwn(COLOUR_GROUP_BY_SOURCE, key)
    ? COLOUR_GROUP_BY_SOURCE[key as keyof typeof COLOUR_GROUP_BY_SOURCE]
    : null;
}

/**
 * ── The SECOND scale: the fourteen groups the exercise catalogue actually names ──────────────
 *
 * Added 3 September 2026 for the plan library's inspector. Owner decision D5 in
 * `docs/design/kanon-programy-2026-09-03/README.md`: the muscle split is drawn over MOBILE's
 * taxonomy — fourteen `MuscleGroup` values, the top five by share plus an `other` segment carrying
 * the remainder — and the fourteen are NOT clustered back to six on the way in.
 *
 * **It is a second scale, not a widening of the first, and the two must not be folded together.**
 * The six above are the plan BUILDER's display clusters: they answer "which of six bands does this
 * day train", they differ per theme, and their values come from the 31.07 written handoff. These
 * fourteen answer "how does this plan's volume divide across the groups the catalogue names", they
 * are theme-independent because mobile declares one hex per group and draws it on both canvases,
 * and their values are `MUSCLE_COLOR`'s, transcribed. A caller reaching for the wrong one gets a
 * type error, which is the whole reason they are two functions rather than one with a mode.
 *
 * The union is declared here rather than imported for the reason the six are: `shared/` may not
 * import from `entities/`, so `MuscleGroup`'s fifteenth member would be caught at the CALL SITE —
 * every caller passes an `entities/exercise-library` `MuscleGroup`, so a new one stops assigning to
 * this parameter and the build goes red there instead of here.
 */
export type MuscleGroupColourKey =
  | 'chest'
  | 'back'
  | 'shoulders'
  | 'biceps'
  | 'triceps'
  | 'forearms'
  | 'abs'
  | 'obliques'
  | 'quads'
  | 'hamstrings'
  | 'glutes'
  | 'calves'
  | 'fullBody'
  | 'other';

const GROUP_TOKENS: Record<MuscleGroupColourKey, string> = {
  chest: 'var(--mg-chest)',
  back: 'var(--mg-back)',
  shoulders: 'var(--mg-shoulders)',
  biceps: 'var(--mg-biceps)',
  triceps: 'var(--mg-triceps)',
  forearms: 'var(--mg-forearms)',
  abs: 'var(--mg-abs)',
  obliques: 'var(--mg-obliques)',
  quads: 'var(--mg-quads)',
  hamstrings: 'var(--mg-hamstrings)',
  glutes: 'var(--mg-glutes)',
  calves: 'var(--mg-calves)',
  fullBody: 'var(--mg-fullbody)',
  other: 'var(--mg-other)',
};

/**
 * The token one of the fourteen groups draws in.
 *
 * `other` is also the colour of the split's sixth "Inne" segment — the remainder after the top
 * five — because mobile gives the `Other` group and the unattributed bucket the same slate and the
 * two apps draw one athlete.
 */
export function muscleGroupColour(group: MuscleGroupColourKey): string {
  return GROUP_TOKENS[group];
}
